import React, { useState, useEffect } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Toast } from './ui/Toast';
import { 
  Lock, Award, ToggleLeft, 
  BookOpen, Shield, Eye, BrainCircuit,
  Medal, CheckCircle, Target, AlertCircle, XCircle, Save, RotateCcw, HelpCircle,
  ChevronRight, Info, Sliders, TrendingUp, TrendingDown, Minus
} from 'lucide-react';
import { api } from '../services/api';
import EnTeteParametrage from './parametrage/EnTeteParametrage';
import ChargementSection from './parametrage/ChargementSection';
import CarteReglages from './parametrage/CarteReglages';

// Import des sous-composants existants
import ParametresVerrouillageNotes from './ParametresVerrouillageNotes';
import ConfigAppreciations from './ConfigAppreciations';


// ============================================
// Onglet 3 : Champs & Visibilité
// ============================================
const ConfigChampsVisibilite = () => {
  const [configParSection, setConfigParSection] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const sections = [
    { 
      key: 'primaire', 
      label: 'Primaire', 
      color: '#319190',
      champs: [
        { key: 'observation_matiere', label: 'Observation par matière', desc: 'Champ texte libre à côté du badge d\'appréciation dans le tableau des notes' },
        { key: 'appreciation_responsable', label: 'Appréciation du Maître', desc: 'Zone de texte pour l\'appréciation de l\'enseignant principal' },
        { key: 'appreciation_directeur', label: 'Appréciation du Directeur', desc: 'Zone de texte pour l\'appréciation du directeur de l\'établissement' },
        { key: 'observation_parents', label: 'Observation des Parents', desc: 'Zone de texte pour les observations et signatures des parents' },
      ]
    },
    { 
      key: 'college', 
      label: 'Collège', 
      color: '#246990',
      champs: [
        { key: 'observation_matiere', label: 'Observation par matière', desc: 'Champ texte libre à côté du badge d\'appréciation dans le tableau des notes' },
        { key: 'appreciation_responsable', label: 'Appréciation du Conseil de Classe', desc: 'Zone de texte pour l\'appréciation du conseil de classe' },
        { key: 'appreciation_directeur', label: 'Appréciation du Directeur', desc: 'Zone de texte pour l\'appréciation du directeur de l\'établissement' },
        { key: 'observation_parents', label: 'Observation des Parents', desc: 'Zone de texte pour les observations et signatures des parents' },
      ]
    },
    { 
      key: 'lycee', 
      label: 'Lycée', 
      color: '#ae7467',
      champs: [
        { key: 'observation_matiere', label: 'Observation par matière', desc: 'Champ texte libre à côté du badge d\'appréciation dans le tableau des notes' },
        { key: 'appreciation_responsable', label: 'Appréciation du Conseil de Classe', desc: 'Zone de texte pour l\'appréciation du conseil de classe' },
        { key: 'appreciation_directeur', label: 'Appréciation du Directeur', desc: 'Zone de texte pour l\'appréciation du directeur de l\'établissement' },
        { key: 'observation_parents', label: 'Observation des Parents', desc: 'Zone de texte pour les observations et signatures des parents' },
      ]
    },
  ];

  useEffect(() => { chargerConfig(); }, []);

  const chargerConfig = async () => {
    try {
      setLoading(true);
      const result = await api.notesVerrouillage.getConfigChamps();
      if (result.success) {
        const map = {};
        result.data.forEach(row => {
          map[row.section] = {
            observation_matiere: row.observation_matiere === 1,
            appreciation_responsable: row.appreciation_responsable === 1,
            appreciation_directeur: row.appreciation_directeur === 1,
            observation_parents: row.observation_parents === 1,
          };
        });
        setConfigParSection(map);
      }
    } catch (error) {
      console.error('❌ Erreur chargement config champs:', error);
    } finally { setLoading(false); }
  };

  /**
   * Bascule tous les champs d'une section en UNE requete.
   *
   * Le bouton « Tout activer » appelait `handleToggle` en boucle : quatre appels
   * concurrents, quatre toasts qui se remplacaient, et un etat `saving` que le
   * dernier arrive remettait a zero pour tout le monde. Les donnees n'etaient
   * pas en danger — l'API n'ecrit que les champs recus — mais l'ecran mentait
   * sur ce qui etait en cours.
   */
  const handleToggleSection = async (sectionKey, champs, valeur) => {
    const avant = configParSection[sectionKey] || {};
    const apres = {};
    champs.forEach(c => { apres[c.key] = valeur; });

    setConfigParSection(prev => ({ ...prev, [sectionKey]: { ...prev[sectionKey], ...apres } }));
    setSaving(`${sectionKey}-tout`);
    try {
      const result = await api.notesVerrouillage.updateConfigChamps(sectionKey, apres);
      if (result.success) {
        setToast({ show: true, message: 'Configuration mise à jour', type: 'success' });
      } else {
        setConfigParSection(prev => ({ ...prev, [sectionKey]: avant }));
        setToast({ show: true, message: result.error || 'Erreur', type: 'error' });
      }
    } catch (err) {
      setConfigParSection(prev => ({ ...prev, [sectionKey]: avant }));
      setToast({ show: true, message: err.message, type: 'error' });
    } finally { setSaving(null); }
  };

  const handleToggle = async (sectionKey, champKey, forceValue = undefined) => {
    const currentValue = configParSection[sectionKey]?.[champKey] ?? true;
    const newValue = forceValue !== undefined ? forceValue : !currentValue;
    if (newValue === currentValue) return;

    setConfigParSection(prev => ({
      ...prev,
      [sectionKey]: { ...prev[sectionKey], [champKey]: newValue }
    }));

    setSaving(`${sectionKey}-${champKey}`);
    try {
      const result = await api.notesVerrouillage.updateConfigChamps(sectionKey, { [champKey]: newValue });
      if (result.success) {
        setToast({ show: true, message: 'Configuration mise à jour', type: 'success' });
      } else {
        setConfigParSection(prev => ({ ...prev, [sectionKey]: { ...prev[sectionKey], [champKey]: currentValue } }));
        setToast({ show: true, message: result.error || 'Erreur', type: 'error' });
      }
    } catch (err) {
      setConfigParSection(prev => ({ ...prev, [sectionKey]: { ...prev[sectionKey], [champKey]: currentValue } }));
      setToast({ show: true, message: err.message, type: 'error' });
    } finally { setSaving(null); }
  };

  if (loading) return <ChargementSection accent="#246990" libelle="Chargement de la configuration…" />;

  return (
    <div className="space-y-5">
      {toast.show && <Toast message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />}

      {/* Section Header */}
      <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
        <div className="w-10 h-10 bg-gradient-to-br from-[#246990] to-[#1a4f6e] rounded-xl flex items-center justify-center shadow-sm">
          <Eye className="text-white" size={18} />
        </div>
        <div>
          <h2 className="text-[15px] font-bold text-gray-900">Champs & Visibilité</h2>
          <p className="text-xs text-gray-500 mt-0.5">Activez ou désactivez les champs du bulletin par section</p>
        </div>
      </div>

      {sections.map(section => {
        const config = configParSection[section.key] || {};
        const activeCount = section.champs.filter(c => config[c.key] !== false).length;
        const allActive = section.champs.every(c => config[c.key] !== false);

        return (
          <CarteReglages
            key={section.key}
            titre={section.label}
            accent={section.color}
            dense
            compteur={
              <span className="flex items-center gap-3">
                <span className="flex items-center gap-1.5">
                  <span className="flex -space-x-0.5">
                    {section.champs.map((c, i) => (
                      <span key={i} className="w-1.5 h-4 rounded-full first:rounded-l-full last:rounded-r-full"
                        style={{ backgroundColor: config[c.key] !== false ? section.color : '#e5e7eb' }} />
                    ))}
                  </span>
                  <span className="text-xs font-semibold text-gray-500 ml-1">{activeCount}/{section.champs.length}</span>
                </span>
                <button
                  onClick={() => handleToggleSection(section.key, section.champs, !allActive)}
                  className={`text-[11px] px-3 py-1.5 rounded-lg font-semibold transition-all border ${
                    allActive
                      ? 'border-gray-200 text-gray-500 hover:bg-red-50 hover:text-red-600 hover:border-red-200'
                      : 'border-transparent text-white hover:opacity-90'
                  }`}
                  style={!allActive ? { backgroundColor: section.color } : {}}>
                  {allActive ? 'Tout désactiver' : 'Tout activer'}
                </button>
              </span>
            }
          >
            <>
              {section.champs.map(champ => {
                const isActive = config[champ.key] !== false;
                const isSaving = saving === `${section.key}-${champ.key}`;
                return (
                  <div key={champ.key} 
                    className={`flex items-center justify-between px-5 py-4 transition-all ${
                      isActive ? 'bg-white' : 'bg-gray-50/40'
                    } hover:bg-gray-50/70`}>
                    <div className="flex items-center gap-3 flex-1 mr-4">
                      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 transition-colors ${isActive ? '' : 'opacity-30'}`}
                        style={{ backgroundColor: isActive ? section.color : '#9ca3af' }} />
                      <div>
                        <p className={`text-sm font-semibold transition-colors ${isActive ? 'text-gray-800' : 'text-gray-400'}`}>
                          {champ.label}
                        </p>
                        <p className="text-[11px] text-gray-400 mt-0.5 leading-relaxed">{champ.desc}</p>
                      </div>
                    </div>
                    <button onClick={() => handleToggle(section.key, champ.key)} disabled={isSaving}
                      className={`relative w-11 h-6 rounded-full transition-all duration-300 flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                        isSaving ? 'opacity-40' : ''
                      }`}
                      // `focusRingColor` n'est pas une propriete CSS : React
                      // l'ignorait en silence et l'anneau de focus restait gris.
                      style={{ backgroundColor: isActive ? section.color : '#d1d5db' }}
                      aria-pressed={isActive}
                      aria-label={champ.label}>
                      <div className={`absolute top-[3px] w-[18px] h-[18px] bg-white rounded-full shadow-sm transition-transform duration-300 ${
                        isActive ? 'translate-x-[22px]' : 'translate-x-[3px]'
                      }`} />
                    </button>
                  </div>
                );
              })}
            </>
          </CarteReglages>
        );
      })}
    </div>
  );
};


// ============================================
// Onglet 4 : Seuils Outil Décisionnel
// ============================================

const CATEGORIES_DEFAUT = [
  { id: 'excellence', label: 'Excellence', min: 16, max: 20, color: '#059669', bgColor: '#ecfdf5', icon: Medal, desc: 'Félicitations, tableau d\'honneur' },
  { id: 'tres_bien', label: 'Très Bien', min: 14, max: 15.99, color: '#1d4ed8', bgColor: '#eff6ff', icon: CheckCircle, desc: 'Encouragements à maintenir' },
  { id: 'satisfaisant', label: 'Satisfaisant', min: 12, max: 13.99, color: '#ca8a04', bgColor: '#fefce8', icon: Target, desc: 'Résultats corrects, peut progresser' },
  { id: 'alerte', label: 'Alerte', min: 10, max: 11.99, color: '#ea580c', bgColor: '#fff7ed', icon: AlertCircle, desc: 'Vigilance, suivi rapproché' },
  { id: 'difficulte', label: 'En difficulté', min: 0, max: 9.99, color: '#dc2626', bgColor: '#fef2f2', icon: XCircle, desc: 'Convocation parents, plan d\'aide' },
];

const ConfigSeuilsDecisionnels = () => {
  const [seuils, setSeuils] = useState(
    CATEGORIES_DEFAUT.map(c => ({ id: c.id, label: c.label, min: c.min, max: c.max }))
  );
  const [seuilStabilite, setSeuilStabilite] = useState(0.5);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  useEffect(() => { chargerSeuils(); }, []);

  const chargerSeuils = async () => {
    try {
      setLoading(true);
      const result = await api.scolarite.getConfigSeuilsDecisionnels();
      if (result.success && result.data) {
        if (result.data.categories?.length > 0) setSeuils(result.data.categories);
        if (result.data.seuil_stabilite !== undefined) setSeuilStabilite(result.data.seuil_stabilite);
      }
    } catch (error) {
      console.error('❌ Erreur chargement seuils:', error);
    } finally { setLoading(false); }
  };

  const handleSeuilChange = (index, field, value) => {
    const val = parseFloat(value);
    if (isNaN(val) && value !== '') return;

    setSeuils(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value === '' ? '' : val };

      if (field === 'min' && index < updated.length - 1) {
        updated[index + 1] = { ...updated[index + 1], max: val > 0 ? parseFloat((val - 0.01).toFixed(2)) : updated[index + 1].max };
      }
      if (field === 'max' && index > 0) {
        updated[index - 1] = { ...updated[index - 1], min: parseFloat((val + 0.01).toFixed(2)) };
      }

      return updated;
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    for (let i = 0; i < seuils.length; i++) {
      if (seuils[i].min === '' || seuils[i].max === '') {
        setToast({ show: true, message: 'Tous les seuils doivent être renseignés', type: 'error' }); return;
      }
      if (seuils[i].min > seuils[i].max) {
        setToast({ show: true, message: `"${seuils[i].label}" : le seuil min ne peut pas être supérieur au max`, type: 'error' }); return;
      }
    }

    setSaving(true);
    try {
      const result = await api.scolarite.saveConfigSeuilsDecisionnels({
        categories: seuils,
        seuil_stabilite: seuilStabilite
      });
      if (result.success) {
        setToast({ show: true, message: 'Seuils enregistrés avec succès', type: 'success' });
        setHasChanges(false);
      } else {
        setToast({ show: true, message: result.error || 'Erreur lors de la sauvegarde', type: 'error' });
      }
    } catch (err) {
      setToast({ show: true, message: err.message, type: 'error' });
    } finally { setSaving(false); }
  };

  const handleReset = () => {
    setSeuils(CATEGORIES_DEFAUT.map(c => ({ id: c.id, label: c.label, min: c.min, max: c.max })));
    setSeuilStabilite(0.5);
    setHasChanges(true);
  };

  if (loading) return <ChargementSection accent="#7c3aed" libelle="Chargement des seuils…" />;

  return (
    <div className="space-y-5">
      {toast.show && <Toast message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />}

      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#7c3aed] to-[#5b21b6] rounded-xl flex items-center justify-center shadow-sm">
            <BrainCircuit className="text-white" size={18} />
          </div>
          <div>
            <h2 className="text-[15px] font-bold text-gray-900">Seuils de l'Outil Décisionnel</h2>
            <p className="text-xs text-gray-500 mt-0.5">Personnalisez les catégories de classification des élèves</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleReset}
            className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-gray-500 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all">
            <RotateCcw size={13} /> Réinitialiser
          </button>
          <button onClick={handleSave} disabled={saving || !hasChanges}
            className={`flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white rounded-lg border border-transparent transition-all shadow-sm ${
              hasChanges 
                ? 'bg-gradient-to-r from-[#7c3aed] to-[#6d28d9] hover:shadow-md hover:shadow-purple-200' 
                : 'bg-gray-300 cursor-not-allowed shadow-none'
            }`}>
            {saving ? <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white/30 border-t-white" /> : <Save size={13} />}
            Enregistrer
          </button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-gradient-to-r from-purple-50 to-violet-50/50 border border-purple-100 rounded-xl p-4">
        <div className="flex gap-3">
          <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm border border-purple-100">
            <Info className="w-4 h-4 text-[#7c3aed]" />
          </div>
          <div className="text-xs text-gray-600 leading-relaxed">
            <p className="font-semibold text-[#7c3aed] text-[13px] mb-1">Comment fonctionnent les seuils ?</p>
            <p>
              Chaque élève est classé selon sa <span className="font-semibold text-gray-700">dernière moyenne</span>. 
              Les bornes adjacentes s'ajustent automatiquement. 
              Ces seuils sont <span className="font-semibold text-gray-700">indépendants</span> des appréciations de bulletins.
            </p>
          </div>
        </div>
      </div>

      {/* Catégories */}
      <CarteReglages
        titre="Catégories de classification"
        accent="#7c3aed"
        dense
        compteur={`${seuils.length} catégories`}
      >
        <>
          {seuils.map((seuil, index) => {
            const catDef = CATEGORIES_DEFAUT.find(c => c.id === seuil.id);
            const IconComp = catDef?.icon || Target;
            const color = catDef?.color || '#6b7280';
            const bgColor = catDef?.bgColor || '#f3f4f6';
            const desc = catDef?.desc || '';

            return (
              <div key={seuil.id} className="flex items-center px-5 py-4 hover:bg-gray-50/50 transition-colors group">
                {/* Badge catégorie */}
                <div className="flex items-center gap-3 w-56 mr-4 flex-shrink-0">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-105" 
                    style={{ backgroundColor: bgColor }}>
                    <IconComp size={16} style={{ color }} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-bold truncate" style={{ color }}>{seuil.label}</p>
                    <p className="text-[11px] text-gray-400 truncate">{desc}</p>
                  </div>
                </div>

                {/* Min */}
                <div className="flex items-center gap-1.5 mr-3 flex-shrink-0">
                  <label className="text-[11px] text-gray-400 font-medium uppercase tracking-wide w-8">Min</label>
                  <input
                    type="number"
                    value={seuil.min}
                    onChange={(e) => handleSeuilChange(index, 'min', e.target.value)}
                    min="0" max="20" step="0.5"
                    disabled={index === seuils.length - 1}
                    className={`w-[72px] px-2.5 py-2 text-sm font-bold border rounded-lg text-center transition-all ${
                      index === seuils.length - 1
                        ? 'bg-gray-50 border-gray-100 text-gray-300 cursor-not-allowed'
                        : 'border-gray-200 hover:border-gray-300 focus:border-[#7c3aed] focus:ring-2 focus:ring-[#7c3aed]/10 outline-none'
                    }`}
                    style={index !== seuils.length - 1 ? { color } : {}}
                  />
                </div>

                {/* Barre visuelle */}
                <div className="flex-1 mx-4 min-w-0">
                  <div className="h-2.5 rounded-full bg-gray-100 relative overflow-hidden">
                    <div
                      className="absolute top-0 h-full rounded-full transition-all duration-500"
                      style={{
                        backgroundColor: color,
                        opacity: 0.35,
                        left: `${((seuil.min || 0) / 20) * 100}%`,
                        width: `${(((seuil.max || 0) - (seuil.min || 0)) / 20) * 100}%`
                      }}
                    />
                    <div
                      className="absolute top-0 h-full rounded-full transition-all duration-500"
                      style={{
                        backgroundColor: color,
                        opacity: 0.7,
                        left: `${((seuil.min || 0) / 20) * 100}%`,
                        width: `${(((seuil.max || 0) - (seuil.min || 0)) / 20) * 100 * 0.5}%`
                      }}
                    />
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className="text-[10px] text-gray-300 font-medium">{seuil.min}/20</span>
                    <span className="text-[10px] text-gray-300 font-medium">{seuil.max}/20</span>
                  </div>
                </div>

                {/* Max */}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <label className="text-[11px] text-gray-400 font-medium uppercase tracking-wide w-8">Max</label>
                  <input
                    type="number"
                    value={seuil.max}
                    onChange={(e) => handleSeuilChange(index, 'max', e.target.value)}
                    min="0" max="20" step="0.5"
                    disabled={index === 0}
                    className={`w-[72px] px-2.5 py-2 text-sm font-bold border rounded-lg text-center transition-all ${
                      index === 0
                        ? 'bg-gray-50 border-gray-100 text-gray-300 cursor-not-allowed'
                        : 'border-gray-200 hover:border-gray-300 focus:border-[#7c3aed] focus:ring-2 focus:ring-[#7c3aed]/10 outline-none'
                    }`}
                    style={index !== 0 ? { color } : {}}
                  />
                </div>
              </div>
            );
          })}
        </>
      </CarteReglages>

      {/* Seuil de stabilité */}
      <CarteReglages
        titre="Seuil de stabilité"
        accent="#7c3aed"
        compteur={
          <span className="flex items-center gap-2">
            <span className="text-[11px] font-medium text-gray-400">(tendance)</span>
          <span className="relative group inline-flex">
            <HelpCircle size={14} className="text-gray-300 cursor-help hover:text-[#7c3aed] transition-colors" />
            <div className="absolute left-0 bottom-full mb-2 w-72 bg-white text-gray-800 text-xs rounded-xl shadow-xl p-4 hidden group-hover:block z-50 border border-purple-100">
              <div className="flex gap-2.5">
                <BrainCircuit className="w-4 h-4 text-[#7c3aed] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-[#7c3aed] mb-1.5">Qu'est-ce que le seuil de stabilité ?</p>
                  <p className="text-gray-500 leading-relaxed">
                    Définit la <span className="font-semibold text-gray-700">marge de variation</span> pour déterminer si un élève est en progression, stable ou en régression.
                  </p>
                  <div className="mt-2.5 text-[11px] bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                    <span className="font-semibold text-gray-600">Exemple avec ± 0.5 :</span><br/>
                    T1 : 12.00 → T2 : 12.30<br/>
                    Diff : +0.30 → <span className="font-bold text-amber-600">Stable</span> (car &lt; 0.5)
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-2 left-6 w-4 h-4 bg-white border-r border-b border-purple-100 transform rotate-45" />
            </div>
          </span>
          </span>
        }
      >
          <div className="flex items-center gap-6">
            <div className="flex-1">
              <p className="text-sm font-bold text-gray-800">Marge de variation pour "Stable"</p>
              <p className="text-xs text-gray-400 mt-1.5 leading-relaxed">
                Si la différence entre deux périodes est inférieure à cette valeur, l'élève est considéré comme <span className="font-semibold text-gray-600">stable</span>. 
                Au-delà, il est en <span className="text-emerald-600 font-semibold">progression</span> ou en <span className="text-red-500 font-semibold">régression</span>.
              </p>
            </div>
            <div className="flex items-center gap-2.5 bg-gray-50 rounded-xl px-4 py-3 border border-gray-100">
              <span className="text-sm text-gray-400 font-medium">±</span>
              <input
                type="number"
                value={seuilStabilite}
                onChange={(e) => { setSeuilStabilite(parseFloat(e.target.value) || 0); setHasChanges(true); }}
                min="0.1" max="3" step="0.1"
                className="w-16 px-2 py-1.5 text-sm font-bold text-[#7c3aed] bg-white border-2 border-gray-200 rounded-lg text-center hover:border-gray-300 focus:border-[#7c3aed] focus:ring-2 focus:ring-[#7c3aed]/10 outline-none"
              />
              <span className="text-xs text-gray-400 font-medium">pts</span>
            </div>
          </div>

          {/* Aperçu */}
          <div className="mt-5 grid grid-cols-3 gap-3">
            <div className="flex items-center gap-2.5 bg-emerald-50/80 rounded-xl px-3.5 py-3 border border-emerald-100">
              <div className="w-7 h-7 bg-emerald-100 rounded-lg flex items-center justify-center">
                <TrendingUp size={14} className="text-emerald-600" />
              </div>
              <div>
                <p className="text-[11px] font-semibold text-emerald-700">Progression</p>
                <p className="text-[11px] text-emerald-500 font-medium">≥ +{seuilStabilite} pts</p>
              </div>
            </div>
            <div className="flex items-center gap-2.5 bg-amber-50/80 rounded-xl px-3.5 py-3 border border-amber-100">
              <div className="w-7 h-7 bg-amber-100 rounded-lg flex items-center justify-center">
                <Minus size={14} className="text-amber-600" />
              </div>
              <div>
                <p className="text-[11px] font-semibold text-amber-700">Stable</p>
                <p className="text-[11px] text-amber-500 font-medium">±{seuilStabilite} pts</p>
              </div>
            </div>
            <div className="flex items-center gap-2.5 bg-red-50/80 rounded-xl px-3.5 py-3 border border-red-100">
              <div className="w-7 h-7 bg-red-100 rounded-lg flex items-center justify-center">
                <TrendingDown size={14} className="text-red-500" />
              </div>
              <div>
                <p className="text-[11px] font-semibold text-red-700">Régression</p>
                <p className="text-[11px] text-red-400 font-medium">≥ -{seuilStabilite} pts</p>
              </div>
            </div>
          </div>
      </CarteReglages>

      {/* Changements non sauvegardés */}
      {hasChanges && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50/50 border border-amber-200 rounded-xl p-4 flex items-center gap-3 animate-in fade-in duration-300">
          <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <AlertCircle size={15} className="text-amber-600" />
          </div>
          <p className="text-xs text-amber-700 font-semibold">Vous avez des modifications non enregistrées.</p>
          <button onClick={handleSave} disabled={saving}
            className="ml-auto px-4 py-2 bg-gradient-to-r from-[#7c3aed] to-[#6d28d9] text-white text-xs font-semibold rounded-lg hover:shadow-md hover:shadow-purple-200 transition-all">
            {saving ? 'Enregistrement…' : 'Enregistrer maintenant'}
          </button>
        </div>
      )}
    </div>
  );
};


// ============================================
// Composant Principal : Conteneur 4 onglets
// ============================================
const ParametrageCarnetEvaluation = ({ onBackToDashboard }) => {
  // `activeTab` etait tenu dans un etat que personne ne lisait : chaque
  // changement d'onglet re-rendait la page entiere pour rien. Radix garde
  // l'onglet actif lui-meme.

  return (
    <div className="w-full">
      <EnTeteParametrage
        onRetour={onBackToDashboard}
        icone={BookOpen}
        titre="Paramétrage du carnet d'évaluation"
        sousTitre="Déverrouillage, appréciations, visibilité des champs et seuils décisionnels"
      />

      {/* Contenu principal avec Tabs */}
      <div className="w-full">
        <Tabs defaultValue="verrouillage" className="w-full">
          {/* Navigation par onglets */}
          <TabsList className="grid grid-cols-4 mb-6 bg-[#246990]/8 p-1 rounded-xl">
            <TabsTrigger 
              value="verrouillage" 
              className="data-[state=active]:bg-[#246990] data-[state=active]:text-white data-[state=active]:shadow-sm rounded-lg text-gray-600 text-sm font-semibold transition-all"
            >
              <Lock size={15} className="mr-2" />
              <span>Déverrouillage</span>
            </TabsTrigger>

            <TabsTrigger 
              value="appreciations" 
              className="data-[state=active]:bg-[#319190] data-[state=active]:text-white data-[state=active]:shadow-sm rounded-lg text-gray-600 text-sm font-semibold transition-all"
            >
              <Award size={15} className="mr-2" />
              <span>Appréciations</span>
            </TabsTrigger>

            <TabsTrigger 
              value="champs" 
              className="data-[state=active]:bg-[#ae7467] data-[state=active]:text-white data-[state=active]:shadow-sm rounded-lg text-gray-600 text-sm font-semibold transition-all"
            >
              <ToggleLeft size={15} className="mr-2" />
              <span>Champs & Visibilité</span>
            </TabsTrigger>

            <TabsTrigger 
              value="seuils" 
              className="data-[state=active]:bg-[#7c3aed] data-[state=active]:text-white data-[state=active]:shadow-sm rounded-lg text-gray-600 text-sm font-semibold transition-all"
            >
              <BrainCircuit size={15} className="mr-2" />
              <span>Seuils Décisionnels</span>
            </TabsTrigger>
          </TabsList>

          {/* Contenu des onglets */}
          <div className="bg-white rounded-xl shadow-[0_1px_4px_rgba(0,0,0,0.06)] border border-gray-200/80 p-6">
            <TabsContent value="verrouillage" className="mt-0 focus-visible:outline-none">
              <ParametresVerrouillageNotes embedded={true} />
            </TabsContent>

            <TabsContent value="appreciations" className="mt-0 focus-visible:outline-none">
              <ConfigAppreciations onBackToDashboard={null} embedded={true} />
            </TabsContent>

            <TabsContent value="champs" className="mt-0 focus-visible:outline-none">
              <ConfigChampsVisibilite />
            </TabsContent>

            <TabsContent value="seuils" className="mt-0 focus-visible:outline-none">
              <ConfigSeuilsDecisionnels />
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
};

export default ParametrageCarnetEvaluation;